import { BubbleMenu as BaseBubbleMenu } from "@tiptap/react";
import { ReactNode, useCallback, useRef } from "react";
import { Instance, sticky } from "tippy.js";
import { v4 as uuid } from "uuid";

import {
  AlignHorizontalDistributeCenter,
  AlignHorizontalDistributeEnd,
  AlignHorizontalDistributeStart,
  Pencil,
  PencilOff,
} from "lucide-react";
import { MenuProps } from "../../../features/menus/types.js";
import { Icon } from "../../../features/ui/Icon.js";
import { Toolbar } from "../../../features/ui/Toolbar.js";
import { getRenderContainer } from "../../../lib/utils/index.js";
import { VideoBlockWidth } from "./VideoBlockWidth.js";
import i18next from "i18next";

export const VideoBlockMenu = ({ editor, appendTo }: MenuProps): ReactNode => {
  const menuRef = useRef<HTMLDivElement>(null);
  const tippyInstance = useRef<Instance | null>(null);

  const getReferenceClientRect = useCallback(() => {
    const renderContainer = getRenderContainer(editor, "node-videoBlock");
    const rect =
      renderContainer?.getBoundingClientRect() ||
      new DOMRect(-1000, -1000, 0, 0);

    return rect;
  }, [editor]);

  const shouldShow = useCallback(() => {
    const isActive = editor.isActive("videoBlock");

    return isActive;
  }, [editor]);

  const onAlignVideoLeft = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setVideoBlockAlign("left")
      .run();
  }, [editor]);

  const onAlignVideoCenter = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setVideoBlockAlign("center")
      .run();
  }, [editor]);

  const onAlignVideoRight = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setVideoBlockAlign("right")
      .run();
  }, [editor]);

  const onHideCaption = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setVideoBlockCaption(
        editor.isActive("videoBlock", { caption: false }) ? true : false
      )
      .run();
  }, [editor]);

  const onWidthChange = useCallback(
    (value: number) => {
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .setVideoBlockWidth(value)
        .run();
    },
    [editor]
  );

  return (
    <BaseBubbleMenu
      editor={editor}
      pluginKey={`videoBlockMenu-${uuid()}`}
      shouldShow={shouldShow}
      updateDelay={0}
      tippyOptions={{
        offset: [0, 8],
        popperOptions: {
          modifiers: [{ name: "flip", enabled: false }],
        },
        getReferenceClientRect,
        onCreate: (instance: Instance) => {
          tippyInstance.current = instance;
        },
        appendTo: () => {
          return appendTo?.current;
        },
        plugins: [sticky],
        sticky: "popper",
      }}
    >
      <Toolbar.Wrapper shouldShowContent={shouldShow()} ref={menuRef}>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("toggleCaption") || "Hide/Show caption"}
          active={editor.isActive("videoBlock", { caption: false })}
          onClick={onHideCaption}
        >
          <Icon
            icon={
              editor.isActive("videoBlock", { caption: false })
                ? PencilOff
                : Pencil
            }
          />
        </Toolbar.Button>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("alignVideoLeft") || "Align video left"}
          active={editor.isActive("videoBlock", { align: "left" })}
          onClick={onAlignVideoLeft}
        >
          <Icon icon={AlignHorizontalDistributeStart} />
        </Toolbar.Button>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("alignVideoCenter") || "Align video center"}
          active={editor.isActive("videoBlock", { align: "center" })}
          onClick={onAlignVideoCenter}
        >
          <Icon icon={AlignHorizontalDistributeCenter} />
        </Toolbar.Button>
        <Toolbar.Button
          type="button"
          tooltip={i18next.t("alignVideoRight") || "Align video right"}
          active={editor.isActive("videoBlock", { align: "right" })}
          onClick={onAlignVideoRight}
        >
          <Icon icon={AlignHorizontalDistributeEnd} />
        </Toolbar.Button>
        <Toolbar.Divider />
        <VideoBlockWidth
          onChange={onWidthChange}
          value={parseInt(editor.getAttributes("videoBlock").width)}
        />
      </Toolbar.Wrapper>
    </BaseBubbleMenu>
  );
};

export default VideoBlockMenu;
